import { Flex, OneColumnLayout, spaces, useBreakpoint } from '@electricjs/arc';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetEmployeeQuery,
  useGetOrganizationGroupsForEmployeeQuery,
} from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetApplicationsForGroups } from '@/components/People/EmployeeAppsByGroup/useGetApplicationsForGroups';
import { CenteredSpinner } from '@common';
import { useCreateOffboardingMutation } from '@/redux/slices/offboardingApiSlice';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import Applications from '@/components/Offboardings/Applications';
import ChampionReassignment from '@/components/Offboardings/ChampionReassignment';
import Devices from '@/components/Offboardings/Devices';
import ReassignTasks from '@/components/Offboardings/ReassignTasks';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { HRIS_OFFBOARDING_REQUEST_ID_QUERY_PARAM } from '@/constants/onboarding';
import { useUpdateRequestStatus } from '@/hooks/useUpdateRequestStatus';
import {
  AssetStatus,
  RequestStatusEnum,
  SortOrder,
} from '@electricjs/core_entity-client';
import PageNotFound from '../PageNotFound';
import { PageErrorComponent } from '@/components/PageErrorComponent';
import SpinnerWithMessage from 'common/SpinnerWithMessage';
import { useListAssetsV2Query } from '@/redux/slices/assetApiSlice';
import {
  useGetRequestTypeCategoriesQuery,
  useGetOrganizationApplicationsQuery,
} from '@/redux/slices/organizationApiSlice';
import {
  useGetRequestByIdQuery,
  useGetRequestsQuery,
} from '@/redux/slices/requestApiSlice';
import { RequestStatus } from '@/types/requests';
import type { Request } from '@/types/requests';
import { Asset, isRetrievableAsset } from '@/types/assets';
import { getDeviceRetrievalOrderIndex } from '@/constants/devices';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetEmployeeOrgApplicationAccounts } from '@/hooks/useGetEmployeeOrgApplicationAccounts';

enum OffboardingSteps {
  Applications = 1,
  ChampionReassignment = 2,
  ReassignTasks = 3,
  Devices = 4,
}

const NewOffboarding = () => {
  const { t } = useTranslation(['pages', 'common']);
  const { md } = useBreakpoint();
  const { ithOffboardReassignChampions } = useFlags();
  const [formStep, setFormStep] = useState(1);
  const organizationId = useGetOrganizationId();
  const [queryParams] = useSearchParams();
  const employeeId = queryParams.get(SelectionParamKeys.EmployeeId) || '';

  const navigate = useNavigate();

  useEffect(() => {
    if (!employeeId) {
      navigate('/employees');
      setTimeout(() => {
        throw new Error('Employee ID is required');
      }, 0);
    }
  }, [employeeId, navigate]);

  const { showErrorToast } = useGlobalUI();

  const { data: employee, isFetching: isFetchingEmployee } =
    useGetEmployeeQuery(
      {
        employeeId,
        organizationId,
      },
      { skip: !employeeId }
    );

  const {
    data: assetsData,
    isLoading: isLoadingAssets,
    isError: isAssetsError,
  } = useListAssetsV2Query(
    {
      organizationId,
      orderBy: 'name',
      sortOrder: SortOrder.Asc,
      assignedTo: employeeId,
      statuses: [AssetStatus.InUse],
    },
    {
      skip: !organizationId || !employeeId,
    }
  );

  const assets = assetsData?.results;

  const { data: requestTypeCategories, isLoading: isLoadingCategories } =
    useGetRequestTypeCategoriesQuery(
      { organizationId },
      { skip: !organizationId || !ithOffboardReassignChampions }
    );

  const { data: championAppsData, isLoading: isLoadingChampionApps } =
    useGetOrganizationApplicationsQuery(
      { organizationId, championEmployeeId: employeeId },
      { skip: !organizationId || !employeeId || !ithOffboardReassignChampions }
    );

  const { data: openTasksData, isLoading: isLoadingOpenTasks } =
    useGetRequestsQuery(
      {
        organizationId,
        status: RequestStatus.InProgress,
        limit: 1000,
        offset: 0,
      },
      { skip: !organizationId || !employeeId || !ithOffboardReassignChampions }
    );

  const openTasks = useMemo(
    () =>
      (openTasksData?.results ?? []).filter(t => t.assignedTo === employeeId),
    [openTasksData, employeeId]
  );
  const hasOpenTasks = openTasks.length > 0;

  // Use isLoading (not isFetching) so background refetches triggered by champion mutations
  // don't re-evaluate routing while the user is already on the ChampionReassignment step.
  const championCategories = useMemo(
    () =>
      (requestTypeCategories ?? []).filter(cat =>
        cat.champions?.some(c => c.employeeId === employeeId)
      ),
    [requestTypeCategories, employeeId]
  );

  const championApps = useMemo(
    () => championAppsData?.applications ?? [],
    [championAppsData]
  );

  // Frozen snapshots passed to ChampionReassignment so mutations don't shrink the lists.
  const [frozenCategories, setFrozenCategories] = useState<
    typeof championCategories
  >([]);
  const [frozenApplications, setFrozenApplications] = useState<
    typeof championApps
  >([]);
  // Frozen snapshot for the Devices step. The Retrieval substep mutates asset
  // status, which invalidates the assets query — without freezing, the live
  // list would shrink mid-flow and unmount this step.
  const [frozenDeviceAssets, setFrozenDeviceAssets] = useState<Asset[]>([]);
  const [frozenOpenTasks, setFrozenOpenTasks] = useState<Request[]>([]);
  const [champReassignId, setChampReassignId] = useState<string | undefined>(
    undefined
  );

  const isChampion = championCategories.length > 0 || championApps.length > 0;

  /* For the purposes of an offboarding, the employee's returnable assets are
   *  every in-use asset assigned to them, whatever the product type, minus the
   *  non-physical ones (service/warranty/shipping) that can't be handed back.
   *  We use the Assets table so laptops without a device record
   *  (Electric app never installed) are also included.
   *  Assets without a serial number are included too, so the admin can still
   *  process them: the retrieval step lets them add the serial number in-flow
   *  and gates the two shipping options on it until they do.
   */
  const employeeAssets = useMemo(() => {
    const retrievable = (assets ?? []).filter(isRetrievableAsset) as Asset[];
    // Walk laptops first (most common device to reclaim), then tablets,
    // phones, desktops, monitors, then everything else grouped by type.
    return [...retrievable].sort(
      (a, b) =>
        getDeviceRetrievalOrderIndex(a.productType.name) -
        getDeviceRetrievalOrderIndex(b.productType.name)
    );
  }, [assets]);

  // A serial number saved from the retrieval step has to be patched into the
  // frozen snapshot by hand. The underlying assets query does refetch (the
  // asset PATCH invalidates its tag), but the Devices step deliberately reads
  // the snapshot rather than the live list, so the refetch can't reach it.
  const handleAssetSerialNumberUpdated = useCallback(
    (assetId: string, serialNumber: string) => {
      setFrozenDeviceAssets(prev =>
        prev.map(asset =>
          asset.id === assetId ? { ...asset, serialNumber } : asset
        )
      );
    },
    []
  );

  const hasDevices = !!employeeAssets?.length;
  const {
    data: employeeGroupsData,
    isFetching: isFetchingEmployeeGroups,
    isSuccess: isEmployeeGroupsDataLoaded,
  } = useGetOrganizationGroupsForEmployeeQuery(
    {
      organizationId,
      employeeId,
    },
    { skip: !employeeId || isFetchingEmployee }
  );

  // Memoizing groupsData to prevent unnecessary re-renders in useGetApplicationsForGroups
  const groupsData = useMemo(() => {
    return employeeGroupsData ?? [];
  }, [employeeGroupsData]);

  // Skip the applications query if employeeGroups data is not loaded
  const { data: appsByGroup, isLoading: isLoadingAppsByGroup } =
    useGetApplicationsForGroups(groupsData, {
      skip: !isEmployeeGroupsDataLoaded,
    });

  const {
    employeeOrgApplicationAccounts,
    isLoading: isLoadingEmployeeOrgAppAccounts,
  } = useGetEmployeeOrgApplicationAccounts({
    employeeId,
    skip: !employeeId || isFetchingEmployee,
  });

  const hasNoApplications =
    isEmployeeGroupsDataLoaded &&
    !isLoadingAppsByGroup &&
    !isLoadingEmployeeOrgAppAccounts &&
    !appsByGroup?.flatMap(group => group.applications).length &&
    !employeeOrgApplicationAccounts?.length;

  // Was this offboarding initiated from an HRIS offboarding request?
  const hrisOffboardingRequestId =
    queryParams.get(HRIS_OFFBOARDING_REQUEST_ID_QUERY_PARAM) ?? '';

  // The scheduled offboarding flow marks its HRIS task with skip_applications/skip_devices to
  // say it's already handling that part, so the wizard shouldn't show that step again.
  const {
    data: hrisOffboardingTask,
    isFetching: isFetchingHrisOffboardingTask,
  } = useGetRequestByIdQuery(
    { organizationId, requestId: hrisOffboardingRequestId },
    { skip: !organizationId || !hrisOffboardingRequestId }
  );
  const skipApplications = hrisOffboardingTask?.data?.skipApplications ?? false;
  const skipDevices = hrisOffboardingTask?.data?.skipDevices ?? false;

  // Either there's nothing to show, or the scheduled offboarding is already handling it.
  const skipApplicationsStep = hasNoApplications || skipApplications;

  const { updateRequest } = useUpdateRequestStatus({
    requestId: hrisOffboardingRequestId,
  });

  const [submitOffboarding, { isLoading: isSubmittingOffboarding }] =
    useCreateOffboardingMutation();

  const createOffboarding = useCallback(
    async (onSuccess: () => void, reassignTasksToEmployeeId?: string) => {
      if (!employee?.id) {
        return;
      }
      try {
        await submitOffboarding({
          employeeId: employee.id,
          organizationId,
          reassignTasksToEmployeeId: reassignTasksToEmployeeId ?? undefined,
          reassignChampToEmployeeId: champReassignId,
        }).unwrap();

        if (hrisOffboardingRequestId) {
          // Mark the HRIS offboarding request as completed
          await updateRequest({
            newStatus: RequestStatusEnum.Completed,
          });
        }

        onSuccess();
      } catch (error) {
        window?.DD_RUM?.addError(error, {
          location: 'Offboarding submit',
        });
        showErrorToast({
          id: 'offboard-employee-error-toast',
          message: t('pages:offboarding.error'),
        });
      }
    },
    [
      employee,
      organizationId,
      showErrorToast,
      submitOffboarding,
      updateRequest,
      hrisOffboardingRequestId,
      t,
      champReassignId,
    ]
  );

  const isLastStepBeforeFinish = !hasDevices || skipDevices;

  // Single commit point — fires createOffboarding then advances past the reassignment steps.
  const createAndAdvance = useCallback(
    (reassignTasksToEmployeeId?: string) => {
      createOffboarding(() => {
        if (hasDevices && !skipDevices) {
          setFrozenDeviceAssets(employeeAssets);
          setFormStep(OffboardingSteps.Devices);
        } else {
          navigate(`/employees/${employeeId}`);
        }
      }, reassignTasksToEmployeeId);
    },
    [
      createOffboarding,
      hasDevices,
      skipDevices,
      employeeAssets,
      navigate,
      employeeId,
    ]
  );

  const goToTasksOrFinish = useCallback(() => {
    if (hasOpenTasks) {
      setFrozenOpenTasks(openTasks);
      setFormStep(OffboardingSteps.ReassignTasks);
    } else {
      createAndAdvance();
    }
  }, [hasOpenTasks, openTasks, createAndAdvance]);

  const handlePostApplicationStep = useCallback(() => {
    if (isChampion) {
      setFrozenCategories(championCategories);
      setFrozenApplications(championApps);
      setFormStep(OffboardingSteps.ChampionReassignment);
    } else {
      goToTasksOrFinish();
    }
  }, [isChampion, championCategories, championApps, goToTasksOrFinish]);

  const autoAdvanceCalledRef = useRef(false);

  useEffect(() => {
    if (
      formStep === OffboardingSteps.Applications &&
      skipApplicationsStep &&
      !isFetchingEmployee &&
      !isLoadingAssets &&
      !isLoadingCategories &&
      !isLoadingChampionApps &&
      !isLoadingOpenTasks &&
      !isFetchingHrisOffboardingTask &&
      // A failed assets request leaves `hasDevices` false, which would let
      // this auto-advance straight through to creating the offboarding.
      !isAssetsError &&
      !autoAdvanceCalledRef.current
    ) {
      autoAdvanceCalledRef.current = true;
      handlePostApplicationStep();
    }
  }, [
    skipApplicationsStep,
    formStep,
    isFetchingEmployee,
    isLoadingAssets,
    isLoadingCategories,
    isLoadingChampionApps,
    isLoadingOpenTasks,
    isFetchingHrisOffboardingTask,
    isAssetsError,
    handlePostApplicationStep,
  ]);

  if (
    isFetchingEmployee ||
    isFetchingEmployeeGroups ||
    isLoadingAppsByGroup ||
    isLoadingEmployeeOrgAppAccounts ||
    isLoadingAssets ||
    isLoadingCategories ||
    isLoadingChampionApps ||
    isLoadingOpenTasks ||
    isFetchingHrisOffboardingTask
  ) {
    return <CenteredSpinner mt={md ? spaces.space_2xl_rem : '2rem'} />;
  }

  if (isSubmittingOffboarding) {
    return (
      <SpinnerWithMessage
        message={t('pages:offboarding.offboardingEmployee')}
        mt={md ? spaces.space_2xl_rem : '2rem'}
      />
    );
  }

  // Hard stop rather than a toast: if we can't tell the offboarder which
  // assets the employee holds, letting them complete the offboarding would
  // leave every one of those assets assigned to someone who has left.
  //
  // Scoped to before the Devices step on purpose. Each retrieval submit
  // PATCHes an asset, which invalidates this query and triggers a refetch —
  // that's exactly why the Devices step reads `frozenDeviceAssets` instead of
  // the live list. Blocking on a failed refetch there would tear down a
  // half-finished walkthrough and strand the assets still to be processed.
  if (isAssetsError && formStep !== OffboardingSteps.Devices) {
    return (
      <PageErrorComponent
        title={t('pages:offboarding.assetsError.title')}
        message={t('pages:offboarding.assetsError.message')}
      />
    );
  }

  if (!employee) {
    return <PageNotFound />;
  }

  return (
    <OneColumnLayout>
      <Flex maxWidth="90rem" margin="0 auto" vertical>
        {formStep === OffboardingSteps.Applications && (
          <Applications
            appsByGroup={appsByGroup}
            isSubmitting={isSubmittingOffboarding}
            onNext={handlePostApplicationStep}
          />
        )}
        {formStep === OffboardingSteps.ChampionReassignment &&
          (frozenCategories.length > 0 || frozenApplications.length > 0) && (
            <ChampionReassignment
              categories={frozenCategories}
              applications={frozenApplications}
              employee={employee}
              organizationId={organizationId}
              onNext={champId => {
                setChampReassignId(champId ?? undefined);
                goToTasksOrFinish();
              }}
              onBack={() =>
                skipApplicationsStep
                  ? navigate(`/employees/${employeeId}`)
                  : setFormStep(OffboardingSteps.Applications)
              }
            />
          )}
        {formStep === OffboardingSteps.ReassignTasks &&
          frozenOpenTasks.length > 0 && (
            <ReassignTasks
              tasks={frozenOpenTasks}
              employee={employee}
              organizationId={organizationId}
              onNext={reassignToId =>
                createAndAdvance(reassignToId ?? undefined)
              }
              onBack={() => {
                // Check frozen snapshots (not live isChampion) so a mid-flow
                // champion mutation can't make Back skip past the
                // ChampionReassignment step the user just came from.
                const cameFromChampionStep =
                  frozenCategories.length > 0 || frozenApplications.length > 0;
                if (cameFromChampionStep) {
                  setFormStep(OffboardingSteps.ChampionReassignment);
                } else if (skipApplicationsStep) {
                  navigate(`/employees/${employeeId}`);
                } else {
                  setFormStep(OffboardingSteps.Applications);
                }
              }}
              isLastStep={isLastStepBeforeFinish}
            />
          )}
        {formStep === OffboardingSteps.Devices &&
          frozenDeviceAssets.length > 0 &&
          !skipDevices && (
            <Devices
              assets={frozenDeviceAssets}
              employee={employee}
              onAssetSerialNumberUpdated={handleAssetSerialNumberUpdated}
            />
          )}
      </Flex>
    </OneColumnLayout>
  );
};

export default NewOffboarding;
